import { useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Logo from '/assets/logo/logo_white.svg';
import { isWeb } from '~/utils/helper/device';
import { invokeChannel, navigator, routePath } from '~/utils/constants';
import { getterIsValidLogin, getterOwnInfo } from '~/store/user/userGetter';
import { useAppDispatch } from '~/store';
import { StyledHeaderBg, header } from './Header.style';
import { displayName } from '~/utils/helper/display';
import { AccountCircleRounded, ListRounded } from '@mui/icons-material';
import {
  BaseButton,
  BASE_BUTTON_VARIANT,
  BASE_BUTTON_COLOR,
} from '~/components/Global/Button/BaseButton';
import { logout } from '~/store/user';
import {
  getNaniClubOidcAuthUrl,
  shouldUseNaniClubOidc,
} from '~/utils/helper/naniClub';
import { resetRecommendSetting } from '~/store/aiAssistant';
import { appNavEvent } from '~/tracking';
import { useOfflineMode } from '~/contexts/OfflineModeContext';

type TProp = {
  setIsSideBarShown: React.Dispatch<React.SetStateAction<boolean>>;
  setButtonRef: React.Dispatch<
    React.SetStateAction<HTMLButtonElement | undefined>
  >;
};

export const Header = ({ setIsSideBarShown, setButtonRef }: TProp) => {
  const navigate = useNavigate();
  const locationObj = useLocation();
  const { pathname } = locationObj;
  const isValidLogin = useSelector(getterIsValidLogin);
  const ownInfo = useSelector(getterOwnInfo);
  const dispatch = useAppDispatch();
  const { isOffline, offlineDisplayName } = useOfflineMode();
  const onlineDisplayName = ownInfo && isValidLogin ? displayName(ownInfo) : '';
  const shownDisplayName = isOffline ? offlineDisplayName : onlineDisplayName;
  // 有沒有名字可顯示（控制頭像與名稱區塊）
  const showDisplayName = Boolean(shownDisplayName);
  // 是不是已登入（控制按鈕文字與點擊行為）。離線頁讀不到登入網域的 cookie，
  // isValidLogin 一定是 false，只能以快取下來的名稱推斷。
  // 這兩者必須分開：displayName 取 name || username，兩者皆空時仍可能是已登入
  // 帳號，若拿它當登入判斷，按鈕會寫「登入」卻執行登出。
  const isLoggedIn = isOffline ? Boolean(offlineDisplayName) : isValidLogin;

  useEffect(() => {
    if (isWeb || isOffline || !onlineDisplayName) return;

    void window.api.invoke(
      invokeChannel.setOfflineDisplayName,
      onlineDisplayName,
    );
  }, [isOffline, onlineDisplayName]);

  return (
    <>
      <StyledHeaderBg path={pathname} id="header">
        {pathname === routePath.login && !isOffline ? (
          <header.LoginTitle>南一會員登入</header.LoginTitle>
        ) : (
          <>
            <header.Container>
              <header.LogoContainer>
                <header.ImageContainer>
                  <header.Image
                    src={Logo}
                    alt="NaniBox3"
                    onClick={() => {
                      appNavEvent({ tab_name: '點擊Logo返回' });
                      if (pathname === routePath.resource) {
                        location.reload();
                      } else {
                        navigate(routePath.home);
                      }
                    }}
                  />
                  {/* 離線頁不顯示版號（PM 確認）。版號是 build 時寫死的，
                      而離線頁用的是安裝檔內的建置、沒有帶入該變數，顯示出來
                      會是 vundefined；且它與線上版號本來就是兩套編號。 */}
                  {!isOffline && (
                    <header.Version>
                      {`v${import.meta.env.VITE_PUBLIC_VERSION}`}
                    </header.Version>
                  )}
                </header.ImageContainer>
                {pathname === routePath.resource && (
                  <BaseButton
                    variant={BASE_BUTTON_VARIANT.FILLED}
                    color={BASE_BUTTON_COLOR.WHITE}
                    text="選單"
                    icon={<ListRounded />}
                    onClick={() => setIsSideBarShown(prev => !prev)}
                    ref={ref => setButtonRef(ref as HTMLButtonElement)}
                  />
                )}
              </header.LogoContainer>
              <header.Nav>
                {(isWeb
                  ? navigator.filter(nav => !nav.onlyElectron)
                  : navigator
                ).map(navi => {
                  const { text, icon, path } = navi;
                  return (
                    <header.Link key={text}>
                      <NavLink
                        to={path}
                        onClick={() => appNavEvent({ tab_name: text })}
                      >
                        {icon}
                        {text}
                      </NavLink>
                    </header.Link>
                  );
                })}
              </header.Nav>
              <header.Login>
                {showDisplayName && (
                  <>
                    <AccountCircleRounded />
                    <header.User>{shownDisplayName}</header.User>
                  </>
                )}
                <header.LoginBtn
                  variant="outlined"
                  disabled={isOffline}
                  onClick={() => {
                    appNavEvent({ tab_name: isLoggedIn ? '登出' : '登入' });

                    if (!isLoggedIn && shouldUseNaniClubOidc()) {
                      window.location.href = getNaniClubOidcAuthUrl();
                    } else if (isLoggedIn) {
                      dispatch(logout());
                      dispatch(resetRecommendSetting());
                    } else {
                      navigate(routePath.login);
                    }
                  }}
                >
                  {isLoggedIn ? '登出' : '登入'}
                </header.LoginBtn>
              </header.Login>
            </header.Container>
            <header.BottomNav>
              {(isWeb
                ? navigator.filter(nav => !nav.onlyElectron)
                : navigator
              ).map(navi => {
                const { text, path } = navi;
                return (
                  <header.BottomLink
                    hasManyWords={text.length > 6}
                    key={text}
                    isSelected={path === pathname}
                    isWeb={isWeb}
                  >
                    <NavLink
                      to={path}
                      onClick={() => appNavEvent({ tab_name: text })}
                    >
                      {text}
                    </NavLink>
                  </header.BottomLink>
                );
              })}
            </header.BottomNav>
          </>
        )}
      </StyledHeaderBg>
    </>
  );
};
